/**
 * Shared navigation state for Slide Sense.
 *
 * Used by both the sidebar (SlideSenseSidebar) and the main page
 * (SlideSensePage) to coordinate which view/folder is active.
 *
 * Only holds UI navigation state — data (folders, slides) lives in
 * TanStack Query hooks to avoid dual sources of truth.
 */

import { create } from "zustand"

interface SlideSenseNavState {
  selectedFolderId: string | null
  showMyPresentations: boolean
  activeTab: "home" | "library" | "admin" | "usage" | "help"
  collectionCount: number
  isAdmin: boolean

  selectHome: () => void
  selectFolder: (id: string | null) => void
  selectMyPresentations: () => void
  selectAllSlides: () => void
  selectAdmin: () => void
  selectUsage: () => void
  selectHelp: () => void
  setCollectionCount: (count: number) => void
  setIsAdmin: (isAdmin: boolean) => void
}

export const useSlideSenseNav = create<SlideSenseNavState>((set) => ({
  selectedFolderId: null,
  showMyPresentations: false,
  // Land on the welcome home view by default — the 3-panel library opens
  // when the user picks an action or a folder. (#5)
  activeTab: "home",
  collectionCount: 0,
  isAdmin: false,

  selectHome: () => set({ activeTab: "home", showMyPresentations: false, selectedFolderId: null }),
  selectFolder: (id) =>
    set({ selectedFolderId: id, showMyPresentations: false, activeTab: "library" }),
  selectMyPresentations: () =>
    set({ showMyPresentations: true, selectedFolderId: null, activeTab: "library" }),
  selectAllSlides: () =>
    set({ selectedFolderId: null, showMyPresentations: false, activeTab: "library" }),
  selectAdmin: () =>
    set({ activeTab: "admin", showMyPresentations: false, selectedFolderId: null }),
  selectUsage: () =>
    set({ activeTab: "usage", showMyPresentations: false, selectedFolderId: null }),
  selectHelp: () => set({ activeTab: "help", showMyPresentations: false, selectedFolderId: null }),
  setCollectionCount: (count) => set({ collectionCount: count }),
  setIsAdmin: (isAdmin) => set({ isAdmin }),
}))
